import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { formatDateTime, formatNum, formatKg } from "../lib/nutrition";
import { Carrot, BookOpenText, Plus, ArrowUpRight, Users } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, b, c] = await Promise.all([
        api.get("/ingredients").catch(() => ({ data: [] })),
        api.get("/recipes").catch(() => ({ data: [] })),
        api.get("/auth/users").catch(() => ({ data: [] })),
      ]);
      setIngredients(a.data);
      setRecipes(b.data);
      setUsers(c.data);
    })();
  }, []);

  const recentRecipes = recipes.slice(0, 5);
  const totalKcal = recipes.reduce((s, r) => s + (r.totals?.energy_kcal || 0), 0);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Dashboard</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            Hallo, {user?.full_name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Übersicht deiner Zutaten und Rezepte.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/zutaten/neu" className="flex-1 sm:flex-none">
            <Button variant="outline" className="rounded-sm w-full sm:w-auto" data-testid="dashboard-new-ingredient">
              <Plus size={14} className="mr-2" /> Neue Zutat
            </Button>
          </Link>
          <Link to="/rezepte/neu" className="flex-1 sm:flex-none">
            <Button className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm w-full sm:w-auto" data-testid="dashboard-new-recipe">
              <Plus size={14} className="mr-2" /> Neues Rezept
            </Button>
          </Link>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Carrot} label="Zutaten" value={ingredients.length} testid="stat-ingredients" />
        <StatCard icon={BookOpenText} label="Rezepte" value={recipes.length} testid="stat-recipes" />
        <StatCard icon={ArrowUpRight} label="Σ kcal (alle Rezepte)" value={formatNum(totalKcal)} testid="stat-kcal" />
        <StatCard icon={Users} label="Benutzer" value={users.length} testid="stat-users" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-zinc-200 rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold">Zuletzt erstellte Rezepte</h2>
            <Link to="/rezepte" className="text-xs text-[#002FA7] hover:underline">Alle ansehen →</Link>
          </div>
          {recentRecipes.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500" data-testid="empty-recipes">
              Noch keine Rezepte. <Link to="/rezepte/neu" className="text-[#002FA7] underline">Jetzt anlegen</Link>.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
                  <th className="px-5 py-2 font-medium">Rezept</th>
                  <th className="px-5 py-2 font-medium text-right">Gewicht</th>
                  <th className="px-5 py-2 font-medium text-right">kcal</th>
                  <th className="px-5 py-2 font-medium">Erstellt von</th>
                  <th className="px-5 py-2 font-medium">Datum</th>
                </tr>
              </thead>
              <tbody>
                {recentRecipes.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-5 py-3">
                      <Link to={`/rezepte/${r.id}`} className="font-medium hover:text-[#002FA7]">
                        {r.name}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-right font-mono-num">{formatKg(r.total_grams)} kg</td>
                    <td className="px-5 py-3 text-right font-mono-num">{formatNum(r.totals?.energy_kcal || 0)}</td>
                    <td className="px-5 py-3 text-zinc-600">{r.created_by_name}</td>
                    <td className="px-5 py-3 text-zinc-500 font-mono-num text-xs">{formatDateTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        <div className="border border-zinc-200 rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-200">
            <h2 className="font-heading text-base font-bold">Team</h2>
          </div>
          <ul className="divide-y divide-zinc-100">
            {users.map((u) => (
              <li key={u.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-bold text-zinc-600">
                  {u.full_name?.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                </div>
                <div>
                  <div className="font-medium">{u.full_name}</div>
                  <div className="text-[11px] text-zinc-500">@{u.username}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-5 hover:border-zinc-300 transition-colors" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-widest">
        <Icon size={14} /> {label}
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2 tracking-tight font-mono-num">{value}</div>
    </div>
  );
}
